import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, CheckCircle2, FileSpreadsheet, Loader2, X, XCircle } from "lucide-react";
import { getBulkUploadDetails } from "../api/notificationsAPI";

/**
 * Popup listing created/skipped/failed households for a bulk Excel upload notification.
 */
function BulkUploadDetailsModal({ notification, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!notification?.id) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setDetails(null);
    getBulkUploadDetails(notification.id)
      .then((data) => {
        if (!cancelled) setDetails(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err?.response?.data?.detail ||
              "We could not load the upload details right now. Please try again.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [notification?.id]);

  if (!notification) return null;

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const skipped = details?.skipped_households || [];
  const failed = details?.failed_households || [];
  const allCreated = !loading && !error && skipped.length === 0 && failed.length === 0;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={handleBackdrop}
      role="presentation"
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto border border-gray-100"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-upload-details-title"
      >
        <div className="h-1 bg-gradient-to-r from-emerald-500 to-teal-500" />
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex gap-3 min-w-0">
              <div className="flex-shrink-0 w-11 h-11 rounded-full bg-emerald-50 flex items-center justify-center border border-emerald-100">
                <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <h3 id="bulk-upload-details-title" className="text-lg font-semibold text-gray-900">
                  Bulk Upload Summary
                </h3>
                {details?.file_name && (
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{details.file_name}</p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {loading && (
            <div className="py-10 flex flex-col items-center text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
              <p className="text-sm">Loading upload details...</p>
            </div>
          )}

          {!loading && error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-100 text-red-600 rounded-lg text-sm">
              {error}
            </div>
          )}

          {!loading && !error && details && (
            <>
              <div className="mt-4 grid grid-cols-3 gap-2">
                <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-center">
                  <p className="text-lg font-bold text-emerald-700">{details.created_count}</p>
                  <p className="text-[11px] font-medium text-emerald-800 uppercase tracking-wide">Created</p>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-center">
                  <p className="text-lg font-bold text-amber-700">{details.skipped_count}</p>
                  <p className="text-[11px] font-medium text-amber-800 uppercase tracking-wide">Duplicate</p>
                </div>
                <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-center">
                  <p className="text-lg font-bold text-red-700">{details.failed_count}</p>
                  <p className="text-[11px] font-medium text-red-800 uppercase tracking-wide">Failed</p>
                </div>
              </div>

              {allCreated && (
                <div className="mt-5 flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <p className="text-sm text-emerald-800">
                    All households from this upload were created successfully.
                  </p>
                </div>
              )}

              <div className="mt-4 max-h-72 overflow-y-auto space-y-4 pr-1">
                {skipped.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      <h4 className="text-sm font-semibold text-gray-800">Duplicate households</h4>
                    </div>
                    <div className="flex gap-1.5 overflow-x-auto pb-1.5">
                      {skipped.map((name, idx) => (
                        <span
                          key={`${name}-${idx}`}
                          className="flex-shrink-0 px-3 py-1.5 bg-amber-50/60 border border-amber-100 rounded-full text-sm font-medium text-gray-900 whitespace-nowrap"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {failed.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <XCircle className="w-4 h-4 text-red-600" />
                      <h4 className="text-sm font-semibold text-gray-800">Failed households</h4>
                    </div>
                    <div className="flex gap-1.5 overflow-x-auto pb-1.5">
                      {failed.map((name, idx) => (
                        <span
                          key={`${name}-${idx}`}
                          className="flex-shrink-0 px-3 py-1.5 bg-red-50/60 border border-red-100 rounded-full text-sm font-medium text-gray-900 whitespace-nowrap"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

            </>
          )}

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default BulkUploadDetailsModal;
